import React, { useState, useEffect, useRef } from 'react';
import { messageAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const MessageThread = ({ messageId, currentUserType, onNewReply }) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const { t, language } = useLanguage();
  const dateLocale = language === 'uz' ? 'uz-UZ' : language === 'ru' ? 'ru-RU' : 'en-US';

  useEffect(() => {
    messageAPI.getReplies(messageId)
      .then(res => setReplies(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [messageId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await messageAPI.sendReply(messageId, text.trim());
      setReplies(prev => [...prev, res.data]);
      setText('');
      onNewReply && onNewReply();
    } catch {
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div style={s.loading}>{t('messages.loadingThread')}</div>;

  return (
    <div style={s.thread}>
      {replies.length > 0 && (
        <div style={s.replies}>
          {replies.map(r => {
            const isMe = r.sender_type === currentUserType;
            return (
              <div key={r.id} style={{ ...s.bubble, ...(isMe ? s.bubbleMe : s.bubbleThem) }}>
                <div style={{ ...s.bubbleName, textAlign: isMe ? 'right' : 'left' }}>
                  {isMe ? t('messages.you') : r.sender_name}
                </div>
                <div style={{ ...s.bubbleText, backgroundColor: isMe ? '#1a365d' : '#f1f5f9', color: isMe ? 'white' : '#2d3748' }}>
                  {r.content}
                </div>
                <div style={{ ...s.bubbleDate, textAlign: isMe ? 'right' : 'left' }}>
                  {new Date(r.created_at).toLocaleString(dateLocale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      )}

      <div style={s.replyBox}>
        <textarea
          style={s.textarea}
          placeholder={t('messages.writeReplyPlaceholder')}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={3}
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(); }}
        />
        <div style={s.replyFooter}>
          <span style={s.hint}>{t('messages.sendShortcutHint')}</span>
          <button
            style={{ ...s.sendBtn, opacity: sending || !text.trim() ? 0.6 : 1 }}
            onClick={handleSend}
            disabled={sending || !text.trim()}
          >
            {sending ? t('messages.sending') : t('messages.replyBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

const s = {
  thread: { marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' },
  loading: { color: '#a0aec0', fontSize: '0.85rem', padding: '0.5rem 0' },
  replies: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', maxHeight: '320px', overflowY: 'auto' },
  bubble: { display: 'flex', flexDirection: 'column', maxWidth: '75%' },
  bubbleMe: { alignSelf: 'flex-end' },
  bubbleThem: { alignSelf: 'flex-start' },
  bubbleName: { fontSize: '0.75rem', color: '#a0aec0', marginBottom: '0.2rem' },
  bubbleText: { padding: '0.65rem 0.9rem', borderRadius: '12px', fontSize: '0.9rem', lineHeight: '1.55', wordBreak: 'break-word' },
  bubbleDate: { fontSize: '0.7rem', color: '#a0aec0', marginTop: '0.2rem' },
  replyBox: { backgroundColor: '#f7fafc', borderRadius: '10px', padding: '0.75rem' },
  textarea: { width: '100%', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.6rem 0.8rem', fontSize: '0.9rem', outline: 'none', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box', backgroundColor: 'white' },
  replyFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' },
  hint: { fontSize: '0.75rem', color: '#a0aec0' },
  sendBtn: { backgroundColor: '#1a365d', color: 'white', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '7px', fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer' },
};

export default MessageThread;
